import { Box, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import Button from '../../../components/common/Button';
import SelectField from '../../../components/common/SelectField';
import TextField from '../../../components/common/TextField';
import ApplicantTable from '../../../components/module/rec/applicant/ApplicantTable';

interface ApplicantTableProps {
  status: string | null;
  source: string | null;
  vacancyCode: string;
  vacancyOrName: string;
  issueId: string | null;
  createdDate: Dayjs | null;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const ApplicantSearch: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filterParams, setFilterParams] = useState<Omit<ApplicantTableProps, 'page' | 'rowsPerPage' | 'onPageChange' | 'onRowsPerPageChange'>>({
    status: '',
    source: '',
    vacancyCode: '',
    vacancyOrName: '',
    issueId: '',
    createdDate: null,
  });
  const [searchParams, setSearchParams] = useState({
    name: '',
    vacancy: '',
    source: '',
    status: '',
    createdDate: null as Dayjs | null,
    issueId: '',
  });

  const handleSearch = () => {
    setFilterParams({
      status: searchParams.status,
      source: searchParams.source,
      vacancyCode: searchParams.vacancy,
      vacancyOrName: searchParams.name,
      issueId: searchParams.issueId,
      createdDate: searchParams.createdDate,
    });
    // Reset to first page when searching
    setPage(0);
  };

  const handleReset = () => {
    setSearchParams({
      name: '',
      vacancy: '',
      source: '',
      status: '',
      createdDate: null,
      issueId: '',
    });
    setFilterParams({
      status: '',
      source: '',
      vacancyCode: '',
      vacancyOrName: '',
      createdDate: null,
      issueId: '',
    });
    // Reset to first page when clearing
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box className="page-container" sx={{ py: '0 !important' }}>
      <Box className="search-section">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '24px' }}>
          <Typography className="page-title">
            Applicant Search
          </Typography>
        </Box>
        <Box className="search-bar">
          <Box style={{ flex: 2 }}>
            <TextField
              placeholder="Search By Vacancy/First Name/Last Name"
              value={searchParams.name}
              onChange={(e) => setSearchParams({ ...searchParams, name: e.target.value })}
              width="100%"
            />
          </Box>
          <Box style={{ flex: 1 }}>
            <TextField
              placeholder="Code"
              width="100%"
              value={searchParams.vacancy}
              onChange={(e) => setSearchParams({ ...searchParams, vacancy: e.target.value as string })}
            />
          </Box>
          <Box style={{ flex: 1 }}>
            <SelectField
              placeholder="Source"
              width="100%"
              value={searchParams.source}
              onChange={(e) => setSearchParams({ ...searchParams, source: e.target.value as string })}
              options={[
                { value: 'direct', label: 'Direct' },
                { value: 'linkedin', label: 'LinkedIn' },
                { value: 'referral', label: 'Referral' },
                { value: 'agency', label: 'Agency' },
                { value: 'website', label: 'Website' },
              ]}
            />
          </Box>
          <Box style={{ flex: 1 }}>
            <TextField
              placeholder="Jira issue ID"
              width="100%"
              value={searchParams.issueId}
              onChange={(e) => setSearchParams({ ...searchParams, issueId: e.target.value as string })}
            />
          </Box>
          <Box style={{ flex: 1 }}>
            <SelectField
              placeholder="Status"
              value={searchParams.status}
              width="100%"
              onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value as string })}
              options={[
                { value: 'OpenCV', label: 'Open CV' },
                { value: 'shortlisted', label: 'Shortlisted' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'joined', label: 'Joined' },
              ]}
            />
          </Box>
          <Box className="search-bar-button-group">
            <Button
              variant="contained"
              onClick={handleSearch}
              width="100px"
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              width="100px"
            >
              Clear
            </Button>
          </Box>
        </Box>
        <ApplicantTable
          status={filterParams.status}
          source={filterParams.source}
          vacancyCode={filterParams.vacancyCode}
          vacancyOrName={filterParams.vacancyOrName}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </Box>
  );
};

export default ApplicantSearch;